import React, { useEffect, useState } from "react";
import { getNiceToHaves } from "../../../services/commonServices";
import { formatResponse } from "../../../helpers/common";
import { BUY, BUY_LAND } from "../../../constants/common";
import PlaceHolderIcon from "../../../images/dpdown-placeholder-icon.svg";
import "./NiceToHaveSuggestions.scss";

const POPULAR_KEYWORDS = [
  "pool",
  "hardwood floor",
  "vaulted ceiling",
  "high ceiling",
  "open floor plan",
  "updated kitchen",
  "office",
  "study",
  "den",
  "fenced yard",
  "large yard",
  "fireplace",
  "walk-in closet",
  "granite",
  "quartz",
  "island",
  "covered patio",
  "patio",
  "finished basement",
  "two car garage",
  "3 car garage",
  "stainless steel",
];

const popularRank = (label) => {
  const l = String(label || "").toLowerCase();
  const idx = POPULAR_KEYWORDS.findIndex((k) => l.includes(k));
  return idx === -1 ? Infinity : idx;
};

const resolveIcon = (icon) => {
  if (!icon) return PlaceHolderIcon;
  try {
    return require(`../../../images/${icon}`);
  } catch (e) {
    return PlaceHolderIcon;
  }
};

// Saved rows come back from the API as { id, tab } with no `value`.
const rowKey = (tab, id) => `${tab}:${Number(id)}`;

const NiceToHaveSuggestions = ({
  propertyType,
  adType,
  selectedNiceToHave = [],
  handleFormValues,
}) => {
  const [options, setOptions] = useState([]);
  const [off, setOff] = useState({});

  useEffect(() => {
    if (!propertyType) return;
    const param = (Number(propertyType) === 6 ? BUY_LAND : adType) || BUY;
    getNiceToHaves(param)
      .then((response) => {
        const result = response && response.result;
        if (!result) return;
        const flat = [];
        Object.keys(result).forEach((category) => {
          formatResponse(result[category]).forEach((opt) => {
            if (opt && opt.value != null) {
              flat.push({
                value: opt.value,
                label: opt.label,
                tab: category,
                icon: opt.Icon || opt.icon || null,
                row: opt,
              });
            }
          });
        });
        setOptions(flat);
      })
      .catch(() => {});
  }, [propertyType, adType]);

  const selected = Array.isArray(selectedNiceToHave) ? selectedNiceToHave : [];
  const selectedKeys = new Set(
    selected.map((n) => rowKey(n.tab, n.value ?? n.id))
  );
  const selectedTotal = selected.length;
  const maxCount = Number(propertyType) === 6 ? 10 : 25;

  const seenLabels = new Set();
  const suggestions = options
    .filter((o) => popularRank(o.label) !== Infinity)
    .filter((o) => !selectedKeys.has(rowKey(o.tab, o.value)))
    .sort((a, b) => popularRank(a.label) - popularRank(b.label))
    .filter((o) => {
      const key = String(o.label || "").toLowerCase().trim();
      if (seenLabels.has(key)) return false;
      seenLabels.add(key);
      return true;
    })
    .slice(0, Math.min(8, maxCount - selectedTotal));

  if (!propertyType || suggestions.length === 0 || selectedTotal >= 6)
    return null;

  const isOn = (key) => !off[key];
  const toggle = (key) =>
    setOff((prev) => ({ ...prev, [key]: !prev[key] }));

  const selectedCount = suggestions.filter((o) =>
    isOn(`${o.tab}:${o.value}`)
  ).length;

  const handleAdd = () => {
    const toAdd = suggestions.filter((o) => isOn(`${o.tab}:${o.value}`));
    let merged = [...selected];
    toAdd.forEach((o) => {
      if (!selectedKeys.has(rowKey(o.tab, o.value))) {
        // Same shape as a dropdown pick, so the select chips get their icons.
        merged.push({ ...o.row, tab: o.tab, id: o.value });
      }
    });
    if (merged.length > maxCount) merged = merged.slice(0, maxCount);
    merged = merged.map((item, index) => ({ ...item, order: index + 1 }));
    handleFormValues({ nice2Have: merged, focusOut: true });
  };

  return (
    <div className="n2h-suggestions">
      <div className="n2h-sugg-head">Most popular nice-to-haves</div>
      <p className="n2h-sugg-sub">
        Common picks agents add for their buyers. Tap to include the ones that
        fit - they sharpen how we match this listing.
      </p>
      <div className="n2h-sugg-chips">
        {suggestions.map((o) => {
          const key = `${o.tab}:${o.value}`;
          const on = isOn(key);
          return (
            <button
              type="button"
              key={key}
              className={`n2h-chip ${on ? "on" : ""}`}
              aria-pressed={on}
              onClick={() => toggle(key)}
            >
              <span className="n2h-chip-badge">
                <svg viewBox="0 0 12 12" width="12" height="12">
                  <path
                    d="M2.5 6.2 L5 8.6 L9.5 3.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <img
                className="n2h-chip-icon"
                src={resolveIcon(o.icon)}
                alt=""
                aria-hidden="true"
              />
              <span className="n2h-chip-label">{o.label}</span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="n2h-sugg-add"
        onClick={handleAdd}
        disabled={selectedCount === 0}
      >
        Add {selectedCount > 0 ? selectedCount : ""} to nice-to-haves
      </button>
    </div>
  );
};

export default NiceToHaveSuggestions;
